import { useState } from "react";
import { ROOTS, WHY_DIFFERENT, TRUTH_WITHIN, FAQS } from "../../data/content";
import { ChevronDown } from "lucide-react";
import { Reveal, motion } from "../../lib/motion";
import { AnimatePresence } from "framer-motion";
import { BuyButton, SectionLabel, Ornament } from "./ui";

export function ProblemSection() {
  return (
    <section id="problem" className="relative py-24 sm:py-36 bg-primary" data-testid="problem-section" data-section="problem" data-section-label="La domanda">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <Reveal><SectionLabel num="01">The Question</SectionLabel></Reveal>
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-editorial italic text-3xl sm:text-5xl lg:text-6xl text-cream leading-[1.05]">
                What if what happened is over — but its effects are not?
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-body font-light text-secondary text-lg leading-relaxed max-w-xl mt-8">
                An event belongs to the past. Yet what it left behind can keep acting in the
                present — in emotions, in the body, in relationships, in posture, in behaviour,
                in the way we perceive ourselves and the patterns we return to.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5 flex flex-col justify-end">
            <Reveal delay={0.2}>
              <div className="gold-top pt-8">
                <p className="font-display text-2xl sm:text-3xl text-gold-light leading-tight">
                  TRAUMA IS NOT ONLY AN EVENT.
                </p>
                <p className="font-display text-2xl sm:text-3xl text-cream leading-tight mt-3">
                  IT CAN ALSO BE A DURATION.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

export function WhatIsSection() {
  return (
    <section id="what" className="relative py-24 sm:py-36 bg-secondary" data-testid="what-section" data-section="what" data-section-label="Cos'è Self Doubling">
      <div className="max-w-5xl mx-auto px-5 sm:px-10">
        <Reveal><SectionLabel num="02">What is Self Doubling?</SectionLabel></Reveal>
        <Reveal>
          <p className="font-editorial text-2xl sm:text-4xl lg:text-5xl text-cream leading-[1.12]">
            <span className="text-gold">Self Doubling©</span> means the act of self doubling, part
            of the method created by <span className="italic">Maria Gioia Mosca</span>.
          </p>
          <p className="font-editorial italic text-xl sm:text-2xl lg:text-3xl text-secondary leading-snug mt-4">
            An original development and perspective of the psychodramatic function of the Double.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 gap-x-12 gap-y-6 mt-14">
          {[
            "It is born within psychodrama.",
            "It takes place in an individual setting.",
            "The work is guided by a specifically trained therapist.",
            "The protagonist is accompanied to encounter and express directly its own internal/direct Double, with no external auxiliary ego.",
            "Self Doubling is neither a technique nor something to be done on your own.\nIt is not something that can be learnt by watching a video and imitating what is being done, because it is not about imitation, but about authenticity.",
            "It explores the neurobiology of trauma and the way the body can retain what the mind has not been able to process.",
            "It approaches trauma not only as an event, but as a duration that continues to act in the present.",
            "It opens a transgenerational perspective, where what remained unspoken can finally find its place.",
          ].map((t, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div className="flex gap-4 items-start gold-top pt-5">
                <span className="font-display text-gold text-lg">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-body font-light text-secondary text-base leading-relaxed whitespace-pre-line">{t}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14">
            <BuyButton location="what" size="lg" label="Buy Self Doubling" testId="buy-btn-what" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function RootsSection() {
  return (
    <section id="roots" className="relative py-24 sm:py-36 bg-primary" data-testid="roots-section" data-section="roots" data-section-label="Corpo, Trauma e Memoria">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <Reveal><SectionLabel num="04">The Body, Trauma &amp; Memory</SectionLabel></Reveal>
        <Reveal>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl gold-gradient-text max-w-3xl leading-tight">
            THE BODY, TRAUMA, AND ITS DURATION IN EMBODIED MEMORY
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-6 max-w-2xl">
            <p className="font-editorial italic text-xl sm:text-2xl lg:text-3xl text-gold-light leading-snug whitespace-pre-line">
              {"How much of ourselves do we truly not know?\nHow far does our tolerance really reach?\nHow much do we truly want to meet ourselves?"}
            </p>
            <p className="font-body font-light text-secondary text-base sm:text-lg mt-5">
              These are the questions that surface, quietly, in our thoughts — and often in the
              body long before they reach the mind.
            </p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--gold-line)] mt-14 rounded-lg overflow-hidden">
          {ROOTS.map((r, i) => (
            <motion.div
              key={r.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className="bg-primary p-7 sm:p-8 hover:bg-secondary transition-colors duration-500 group"
              data-testid={`root-card-${i}`}
            >
              <span className="font-label text-gold text-xs">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-editorial text-xl sm:text-2xl text-cream mt-3 group-hover:text-gold-light transition-colors">
                {r.title}
              </h3>
              <p className="font-body font-light text-muted text-sm leading-relaxed mt-3">{r.body}</p>
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}

export function EnergySection() {
  return (
    <section id="energy" className="relative py-24 sm:py-36 bg-primary scroll-mt-24 sm:scroll-mt-28" data-testid="energy-section" data-section="energy" data-section-label="Energia e Depressione">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <Reveal><SectionLabel num="05">Energy for Lasting Change</SectionLabel></Reveal>

        <div className="max-w-3xl">
          <Reveal>
            <h2
              className="font-display text-3xl sm:text-4xl lg:text-5xl gold-gradient-text leading-tight"
              data-testid="energy-radical-title"
            >
              THE IMPORTANCE OF ENERGY IN RADICAL AND LASTING CHANGE
            </h2>
          </Reveal>

          <Reveal delay={0.05}>
            <p
              className="font-editorial italic text-xl sm:text-2xl lg:text-3xl text-gold-light leading-snug mt-5"
              data-testid="energy-radical-subtitle"
            >
              Embodied Memory and Depression
            </p>
          </Reveal>
        </div>

        <Reveal>
          <figure
            className="mx-auto w-full relative mt-10 sm:mt-12"
            style={{
              maxWidth: "1200px",
              aspectRatio: "1.2916 / 1",
            }}
            data-testid="energy-radical-frame"
          >
            <img
              src="https://customer-assets-jai6qajn.emergentagent.net/job_506fa2e7-257d-48e0-9445-6bff0f28d0e9/artifacts/ikzsvy5s_DEFINITIVA%20SITO.png"
              alt="Scientific diagram: the human head with brain anatomy, macrominerals, microminerals, vitamins, neurotransmitters and the citric acid (Krebs) cycle leading to ATP synthesis — an integrated view of the biological substrate of embodied change."
              loading="lazy"
              data-testid="energy-radical-image"
              style={{
                position: "absolute",
                left: "16.638%",
                top: "21.32%",
                width: "67.06%",
                height: "57.74%",
                display: "block",
                objectFit: "fill",
                backgroundColor: "#000",
              }}
            />
            <img
              src="/ornate-frame-ai-h.png"
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 w-full h-full"
              style={{
                opacity: 0.85,
                filter: "saturate(0.88) brightness(0.94)",
              }}
              data-testid="energy-radical-frame-overlay"
            />
          </figure>
        </Reveal>

        <div className="mt-14 sm:mt-16 max-w-2xl mx-auto">
          <Reveal delay={0.1}>
            <div className="mt-10 font-body font-light text-secondary text-base sm:text-lg leading-relaxed">

              {/* — 1. THE BODY IN DEPRESSION — */}
              <div className="flex items-center gap-3 mb-7" data-testid="energy-kicker-1">
                <span className="h-px w-8 bg-[var(--gold-line)]" aria-hidden />
                <span className="font-label text-[0.68rem] tracking-[0.28em] uppercase text-gold-light">The Body in Depression</span>
              </div>
              <p className="first-letter:font-display first-letter:text-[4rem] sm:first-letter:text-[5rem] first-letter:leading-[0.85] first-letter:text-gold first-letter:float-left first-letter:pr-3 first-letter:mt-1">
                In depression, the body is not only the place where distress manifests itself, but also participates directly in the possibility of change.
              </p>
              <p className="mt-5">
                In my work, it is fundamental to distinguish a primary healthy memory of the body from traumatic memories formed by the events of our existence and from transgenerational memories we inherit.
                <br />
                Depression can thus also be understood as an expression of these memories that need to be transformed, while bodily, biological and nutritional work can contribute to reactivating more fundamental resources, so that the organism can once again genuinely participate in the process of change.
              </p>

              {/* — 2. MEMORY & BIOLOGICAL RESOURCES — */}
              <div className="flex items-center gap-3 mt-16 mb-7" data-testid="energy-kicker-2">
                <span className="h-px w-8 bg-[var(--gold-line)]" aria-hidden />
                <span className="font-label text-[0.68rem] tracking-[0.28em] uppercase text-gold-light">Memory &amp; Biological Resources</span>
              </div>
              <p>
                I have explored this subject in depth through more than thirty years of studying the HTMA test – Hair Tissue Mineral Analysis – through which nutritional minerals and toxic metals in the hair are identified.
              </p>
              <p className="mt-5">
                This work has formed part of my broader study of tissue metabolism.
                <br />
                It has enabled me to deepen my understanding of the role of the biological dimension in the processes of balance and change within the organism.
              </p>

              {/* — 3. ENERGY & TRANSFORMATION — */}
              <div className="flex items-center gap-3 mt-16 mb-7" data-testid="energy-kicker-3">
                <span className="h-px w-8 bg-[var(--gold-line)]" aria-hidden />
                <span className="font-label text-[0.68rem] tracking-[0.28em] uppercase text-gold-light">Energy &amp; Transformation</span>
              </div>
              <p>
                For genuine and profound transformation to occur, the organism must be able to produce sufficient energy.
              </p>
              <p className="mt-5">
                Without this biological capacity, change may begin; however, it cannot be sustained over time, and lasting resolution is therefore impossible.
              </p>

              {/* — 4. ETYMOLOGY CALLOUT — */}
              <aside
                className="mt-16 border border-[var(--gold-line)] rounded-lg p-6 sm:p-8 bg-black/40"
                data-testid="energy-etymology-callout"
              >
                <div className="flex items-center gap-3 mb-5">
                  <span className="h-px w-6 bg-[var(--gold-line)]" aria-hidden />
                  <span className="font-label text-[0.65rem] tracking-[0.28em] uppercase text-gold-light">Etymology</span>
                </div>
                <p>
                  Depression, [from Latin <em>depressio, -onis</em>, derived from <em>deprimĕre</em> ‘to depress’, past participle <em>depressus</em>].
                </p>
                <p className="font-normal text-cream mt-4">
                  <strong><em>The act or fact of depressing, that is, of bringing to a lower level.</em></strong>
                </p>
              </aside>

              {/* — 5. THE BODY BROUGHT LOWER — */}
              <div className="flex items-center gap-3 mt-16 mb-7" data-testid="energy-kicker-5">
                <span className="h-px w-8 bg-[var(--gold-line)]" aria-hidden />
                <span className="font-label text-[0.68rem] tracking-[0.28em] uppercase text-gold-light">The Body Brought Lower</span>
              </div>
              <p>
                When applied to the reading of the body, the etymological meaning of depression as ‘bringing to a lower level’ is particularly interesting, because it can be understood not only in psychological terms, but also in bodily terms.
              </p>
              <p className="mt-5">
                In the body, ‘to depress’ may evoke a reduction in tone, available energy, motor initiative, breadth of breathing, postural openness, and the capacity for expansion and response to the environment.
                <br />
                The body may therefore appear, in phenomenological terms, as having been ‘<strong><em>brought lower</em></strong>’: more closed, less toned, less mobile, and less propulsive.
              </p>
              <p className="mt-5">
                This phenomenological framing is consistent with work in psychiatric embodiment theory (Fuchs &amp; Schlimme, 2009).
              </p>
              <p className="mt-5">
                This does not mean that depression can be reduced to posture or to a single bodily mechanism.
              </p>
              <p className="mt-5">
                It does, however, mean that the term itself contains the idea of a lowering of level, which can also be observed in the way the body inhabits space.
              </p>

              {/* — 6. SYNTHESIS — */}
              <div className="flex items-center gap-3 mt-16 mb-7" data-testid="energy-kicker-6">
                <span className="h-px w-8 bg-[var(--gold-line)]" aria-hidden />
                <span className="font-label text-[0.68rem] tracking-[0.28em] uppercase text-gold-light">Synthesis</span>
              </div>
              <p>
                Depression, already in its etymological root, indicates a downward movement, a reduction in level.
              </p>
              <p className="mt-5">
                When applied to the reading of the body, depression may manifest as a loss of tone, energy, openness and capacity to respond.
              </p>

              {/* — PULL QUOTE 1 — */}
              <blockquote
                className="mt-14 relative text-center"
                data-testid="energy-pullquote-1"
              >
                <span aria-hidden className="block font-display text-6xl text-gold-light/40 leading-none">“</span>
                <p className="font-editorial italic text-cream text-xl sm:text-2xl leading-snug mt-1">
                  The body is therefore not merely the place in which depression manifests itself, but participates concretely in the form it takes and in the possibility of transforming it.
                </p>
              </blockquote>

              {/* — PULL QUOTE 2 — */}
              <blockquote
                className="mt-14 relative text-center"
                data-testid="energy-pullquote-2"
              >
                <span aria-hidden className="block font-display text-6xl text-gold-light/40 leading-none">“</span>
                <p className="font-editorial italic text-cream text-xl sm:text-2xl leading-snug mt-1">
                  The most interesting aspect is precisely this: depression is not merely a mental state, but a global lowering of the organism’s capacity to sustain movement, response and change.
                </p>
              </blockquote>

            </div>
          </Reveal>

            <Reveal delay={0.15}>
              <div
                className="mt-10 pt-6 border-t border-[var(--gold-line)]"
                data-testid="energy-radical-bibliography"
              >
                <p className="font-body italic text-secondary text-sm sm:text-base leading-relaxed">
                  Fuchs, T., &amp; Schlimme, J. E. (2009). Embodiment and psychopathology: a phenomenological perspective. Current Opinion in Psychiatry, 22(6), 570–575.{" "}
                  <a
                    href="https://doi.org/10.1097/YCO.0b013e3283318e5c"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gold-light hover:text-gold transition-colors underline underline-offset-4 decoration-[var(--gold-line)] hover:decoration-gold not-italic"
                    data-testid="energy-radical-doi"
                  >
                    https://doi.org/10.1097/YCO.0b013e3283318e5c
                  </a>
                </p>
              </div>
            </Reveal>
        </div>
      </div>
    </section>
  );
}

export function DifferentSection() {
  return (
    <section className="relative py-24 sm:py-36 bg-secondary" data-testid="different-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal><SectionLabel num="04">Why it is Different</SectionLabel></Reveal>
            <Reveal>
              <h2 className="font-editorial italic text-3xl sm:text-4xl lg:text-5xl text-cream leading-tight">
                A change of paradigm in the function of the Double.
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="font-body font-light text-secondary mt-6 leading-relaxed max-w-md">
                Where classical psychodrama places the Double outside the protagonist, Self
                Doubling© turns inward — a direct, intimate encounter with oneself.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7 space-y-4">
            {WHY_DIFFERENT.contrasts.map((c, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="gold-border rounded-xl p-5 sm:p-6 hover:border-[var(--gold)] transition-colors duration-500">
                  <p className="font-body text-sm sm:text-base text-muted line-through decoration-[var(--gold-dark)]/60">
                    {c.not}
                  </p>
                  <p className="font-editorial text-lg sm:text-xl text-cream mt-2 leading-snug">
                    <span className="text-gold">— </span>{c.is}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-wrap gap-x-8 gap-y-3">
          {WHY_DIFFERENT.paradigm.map((p, i) => (
            <Reveal key={p} delay={i * 0.05}>
              <span className="font-label text-[0.7rem] tracking-[0.16em] uppercase text-gold-light gold-border rounded-full px-4 py-2">
                {p}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function NotSelfHelp() {
  return (
    <section className="relative py-24 sm:py-40 bg-primary overflow-hidden" data-testid="notselfhelp-section" data-section="not-self-help" data-section-label="Non è self-help">
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span className="font-display text-[22vw] text-cream opacity-[0.025] whitespace-nowrap">NOT SELF-HELP</span>
      </div>
      <div className="relative max-w-4xl mx-auto px-5 sm:px-10 text-center">
        <Reveal><Ornament className="mb-10" /></Reveal>
        <Reveal>
          <h2 className="font-display text-3xl sm:text-5xl text-cream leading-tight">
            THIS IS NOT A SELF-HELP MANUAL
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="font-editorial italic text-xl sm:text-2xl text-gold-light mt-8 leading-relaxed whitespace-pre-line">
            {"Self Doubling© is not a self-help technique\nand it is not intended for autonomous practice.\nIt requires a trained therapist, a protected setting and previous psychodramatic work."}
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="font-body font-light text-secondary mt-8 max-w-2xl mx-auto leading-relaxed whitespace-pre-line">
            {"The book presents the theory, the research, the methodological path\nand the change of paradigm it proposes."}
          </p>
        </Reveal>
      </div>
    </section>
  );
}


export function TruthWithin() {
  return (
    <section id="truth" className="relative py-24 sm:py-40 bg-secondary overflow-hidden" data-testid="truth-section" data-section="truth" data-section-label="La verità dentro di te">
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[70vh] rounded-full blur-[140px] opacity-[0.1] pointer-events-none"
        style={{ background: "radial-gradient(circle, #aa8656 0%, transparent 70%)" }}
      />
      <div className="relative max-w-4xl mx-auto px-5 sm:px-10">
        <Reveal><SectionLabel num="03">The Truth Within</SectionLabel></Reveal>
        <div className="space-y-16 sm:space-y-20 mt-10">
          {TRUTH_WITHIN.map((t, i) => (
            <Reveal key={i} delay={0.05}>
              <div className="grid sm:grid-cols-12 gap-5 sm:gap-10 items-start">
                <div className="sm:col-span-3">
                  <span className="font-display text-5xl sm:text-6xl gold-gradient-text leading-none">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="font-label text-[0.65rem] tracking-[0.2em] uppercase text-gold-light mt-3">
                    {t.kicker}
                  </p>
                </div>
                <div className="sm:col-span-9">
                  <p className="font-editorial text-xl sm:text-2xl lg:text-[1.7rem] text-cream leading-relaxed whitespace-pre-line">
                    {t.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FaqSection() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-24 sm:py-36 bg-primary" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-5 sm:px-10">
        <Reveal><SectionLabel num="07">Frequently Asked Questions</SectionLabel></Reveal>
        <Reveal>
          <h2 className="font-editorial italic text-3xl sm:text-5xl text-cream leading-tight max-w-2xl">
            Everything you may want to know before you begin.
          </h2>
        </Reveal>

        <div className="mt-12 border-t border-[var(--gold-line)]">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border-b border-[var(--gold-line)]" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                  data-testid={`faq-question-${i}`}
                  aria-expanded={isOpen}
                >
                  <span className={`font-editorial text-xl sm:text-2xl transition-colors ${isOpen ? "text-gold-light" : "text-cream group-hover:text-gold-light"}`}>
                    {f.q}
                  </span>
                  <ChevronDown
                    size={22}
                    className={`shrink-0 text-gold transition-transform duration-400 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <p className="font-body font-light text-secondary text-base leading-relaxed pb-7 max-w-2xl" data-testid={`faq-answer-${i}`}>
                        {f.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-14">
            <BuyButton location="faq" size="lg" testId="buy-btn-faq" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
